import type {FC, ReactElement} from 'react'
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {
  getPreviousShiftRange,
  getRollingRangeStart,
  parseRollingRange,
  PREVIOUS_SHIFT_RANGE_ID,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfShift,
  startOfWeek,
  startOfYear,
} from '@shared/utils/timeRange'

interface TimeIntervalProviderProps {
  children?: ReactElement
  initialTimeRange: string | {startTime: Date; endTime: Date}
  syncUrl?: boolean
}

export interface TimeIntervalContextProps {
  startTime: Date
  endTime: Date
  timeRange: string
  updateTimeInterval: (timeRange: string | null) => void
  updateStartTime: (startTime: Date) => void
  updateEndTime: (endTime: Date) => void
  setTimeInterval: (start: Date, end: Date) => void
  resetTime: () => void
}

const TimeIntervalContext = createContext<TimeIntervalContextProps | null>(null)

const useTimeIntervalContext = (): TimeIntervalContextProps | null =>
  useContext(TimeIntervalContext)

const removeHours = (numOfHours: number, date: Date): Date => {
  const dateCopy = new Date(date.getTime())
  dateCopy.setTime(dateCopy.getTime() - numOfHours * 60 * 60 * 1000)
  return dateCopy
}

/**
 * Where a preset's window ends: now, except for "previous shift" (AB#6936), which is
 * a completed shift and ends at the current shift's start.
 */
const getEndTime = (timeRange: string, now: Date = new Date(Date.now())): Date =>
  timeRange === PREVIOUS_SHIFT_RANGE_ID ? getPreviousShiftRange(now).end : now

// Start and end of one window must be read from the same `now`; two reads can land a
// millisecond apart and change the window's span.
const getStartTime = (timeRange: string, now: Date = new Date(Date.now())): Date => {
  if (timeRange === PREVIOUS_SHIFT_RANGE_ID) {
    return getPreviousShiftRange(now).start
  }
  // "Last N hours / days" with a free N (AB#6936).
  const rolling = parseRollingRange(timeRange)
  if (rolling) {
    return getRollingRangeStart(rolling, now)
  }
  let startDate: Date
  switch (timeRange) {
    case '00:15:00':
      startDate = removeHours(0.25, now)
      break
    case '00:30:00':
      startDate = removeHours(0.5, now)
      break
    case '01:00:00':
      startDate = removeHours(1, now)
      break
    case '04:00:00':
      startDate = removeHours(4, now)
      break
    case '08:00:00':
      startDate = removeHours(8, now)
      break
    case '12:00:00':
      startDate = removeHours(12, now)
      break
    case '1 00:00:00':
      startDate = removeHours(24, now)
      break
    case 'day-so-far':
      startDate = startOfDay(now)
      break
    case 'shift-so-far':
      startDate = startOfShift(now)
      break
    case '2 00:00:00':
      startDate = removeHours(48, now)
      break
    case '3 00:00:00':
      startDate = removeHours(72, now)
      break
    case '7 00:00:00':
      startDate = removeHours(168, now)
      break
    case '14 00:00:00':
      startDate = removeHours(336, now)
      break
    case 'week-so-far':
      startDate = startOfWeek(now)
      break
    case '28 00:00:00':
      startDate = removeHours(672, now)
      break
    case 'month-so-far':
      startDate = startOfMonth(now)
      break
    case 'quarter-so-far':
      startDate = startOfQuarter(now)
      break
    case 'year-so-far':
      startDate = startOfYear(now)
      break
    case 'three-months':
      startDate = removeHours(2016, now)
      break
    case 'six-months':
      startDate = removeHours(4032, now)
      break
    case 'twelve-months':
      startDate = removeHours(8736, now)
      break
    default:
      startDate = removeHours(24, now)
      break
  }
  return startDate
}

const TimeIntervalProvider: FC<TimeIntervalProviderProps> = ({
  children,
  initialTimeRange,
  syncUrl = true,
}): ReactElement => {
  // States
  const [searchParams, setSearchParams] = useSearchParams()
  const timeRangeParam = searchParams.get('time_range')
  const [mountNow] = useState(() => new Date(Date.now()))
  const [startTime, setStartTime] = useState<Date>(() => {
    if (typeof initialTimeRange !== 'string') {
      return initialTimeRange.startTime
    }
    if (syncUrl && searchParams.get('start_date')) {
      return new Date(searchParams.get('start_date') as string)
    }
    // Derive the bound from the URL's OWN preset when it has one. Falling back
    // to initialTimeRange here gave `?time_range=7 00:00:00` (no bounds) a
    // one-day window under a seven-day label — the picker read "last 7 days"
    // while the charts showed 24 h, and the mismatch was then written into the
    // URL and saved into views (AB#6013).
    return getStartTime(syncUrl && timeRangeParam ? timeRangeParam : initialTimeRange, mountNow)
  })
  const [endTime, setEndTime] = useState<Date>(() => {
    if (typeof initialTimeRange !== 'string') {
      return initialTimeRange.endTime
    }
    return syncUrl && searchParams.get('end_date')
      ? new Date(searchParams.get('end_date') as string)
      : getEndTime(syncUrl && timeRangeParam ? timeRangeParam : initialTimeRange, mountNow)
  })
  const [timeRange, setTimeRange] = useState<string>(() => {
    if (typeof initialTimeRange !== 'string') {
      return 'null'
    }
    return syncUrl ? timeRangeParam || initialTimeRange : initialTimeRange
  })

  // Hooks
  const {id} = useParams()

  // Refs
  const startTimeRef = useRef<string>(startTime.toISOString())
  const endTimeRef = useRef<string>(endTime.toISOString())
  const idRef = useRef<string>(id as string)
  const timeRangeRef = useRef<string>(timeRange)

  // Functions
  const updateStartTime = useCallback((startTime: Date) => {
    setStartTime(startTime)
  }, [])

  const updateEndTime = useCallback((endTime: Date) => {
    setEndTime(endTime)
  }, [])

  const updateTimeInterval = useCallback((timeRange: string | null) => {
    const normalizedTimeRange = timeRange ?? 'null'
    setTimeRange(normalizedTimeRange)
    if (normalizedTimeRange === 'null') {
      return
    }
    const now = new Date(Date.now())
    const startDate = getStartTime(normalizedTimeRange, now)
    setEndTime(getEndTime(normalizedTimeRange, now))
    setStartTime(startDate)
  }, [])

  const setTimeInterval = useCallback((start: Date, end: Date) => {
    setStartTime(start)
    setEndTime(end)
    setTimeRange('null')
  }, [])

  const resetTime = useCallback(() => {
    const now = new Date(Date.now())
    setStartTime(getStartTime(timeRange, now))
    setEndTime(getEndTime(timeRange, now))
  }, [timeRange])

  // Effects
  useEffect(() => {
    if (!syncUrl) {
      return
    }
    // On the commit where the route id changed, the URL is the incoming page's,
    // not this state's. Writing state out here would overwrite the window the
    // link brought with it before the effect below gets to read it (AB#6241);
    // that effect owns this commit and the sync resumes on the next one.
    if (idRef.current !== id) {
      return
    }
    const {current: currentStartTime} = startTimeRef
    const {current: currentEndTime} = endTimeRef
    const {current: currentTimeRange} = timeRangeRef
    const newSearchParams = new URLSearchParams(window.location.search)
    const currentStartDate = newSearchParams.get('start_date')
    const currentEndDate = newSearchParams.get('end_date')
    const currentTimeRangeParam = newSearchParams.get('time_range')
    if (currentStartTime !== startTime.toISOString() || currentStartTime !== currentStartDate) {
      newSearchParams.set('start_date', startTime.toISOString())
      startTimeRef.current = startTime.toISOString()
    }
    if (currentEndTime !== endTime.toISOString() || currentEndTime !== currentEndDate) {
      newSearchParams.set('end_date', endTime.toISOString())
      endTimeRef.current = endTime.toISOString()
    }
    if (currentTimeRange !== timeRange || currentTimeRange !== currentTimeRangeParam) {
      newSearchParams.set('time_range', timeRange)
      timeRangeRef.current = timeRange
    }
    setSearchParams(newSearchParams, {replace: true})
  }, [timeRange, startTime, endTime, id, searchParams, setSearchParams, syncUrl])

  useEffect(() => {
    const {current: currentId} = idRef
    if (currentId !== id) {
      // Switching equipment/line changes the route param without remounting
      // the provider, so this is the only place the new page's window is
      // decided. Resetting to initialTimeRange unconditionally threw away the
      // window the switcher had just put in the URL, which is why every switch
      // came back on the page default (AB#6241). Read the URL first and only
      // fall back when it carries no window of its own.
      const urlTimeRange = syncUrl ? searchParams.get('time_range') : null
      const urlStartDate = syncUrl ? searchParams.get('start_date') : null
      const urlEndDate = syncUrl ? searchParams.get('end_date') : null
      if (urlTimeRange === 'null' && urlStartDate && urlEndDate) {
        // 'null' is the custom window; its bounds are the only thing that says
        // what it is, so they have to be applied verbatim rather than derived.
        setTimeInterval(new Date(urlStartDate), new Date(urlEndDate))
      } else if (urlTimeRange) {
        updateTimeInterval(urlTimeRange)
      } else if (typeof initialTimeRange === 'string') {
        updateTimeInterval(initialTimeRange)
      } else {
        setTimeInterval(initialTimeRange.startTime, initialTimeRange.endTime)
      }
    }
    idRef.current = id as string
    // searchParams also changes on every URL sync; the idRef guard above makes
    // those runs a no-op.
  }, [id, searchParams, updateTimeInterval, initialTimeRange, setTimeInterval, syncUrl])

  return (
    <TimeIntervalContext.Provider
      value={useMemo(
        () => ({
          startTime,
          endTime,
          timeRange,
          updateTimeInterval,
          updateStartTime,
          updateEndTime,
          setTimeInterval,
          resetTime,
        }),
        [
          startTime,
          endTime,
          timeRange,
          updateTimeInterval,
          updateStartTime,
          updateEndTime,
          setTimeInterval,
          resetTime,
        ],
      )}
    >
      {children}
    </TimeIntervalContext.Provider>
  )
}

export {useTimeIntervalContext, TimeIntervalProvider, TimeIntervalContext}
